import {useMemo} from 'react'
import type {Markers} from '../stores/slices/markers'
import type {ThreadPreview} from '../page-data/payloads/thread-previews'
import type {PullRequestFileTreeDiff, ThreadMarker} from '../page-data/payloads/file-tree'
import type {ThreadSubject} from '@github-ui/conversations'

/**
 * Stitches thread subjects into thread markers by parsing subject information from different sources.
 *
 * This hook handles two different scenarios based on whether thread previews are being deferred:
 * 1. When deferring: Extracts subject information from thread markers in diff summaries
 * 2. When not deferring: Uses thread previews to populate subject information
 *
 * This is a workaround needed because suggested changes require thread subjects, but the data
 * comes from separate sources. In the future, this logic should be moved to build time when
 * thread previews are removed and state should be stored in Zustand.
 *
 * @param initialMarkers - The markers object containing threads that need subject information
 * @param initialDiffSummaries - Array of diff summaries containing marker maps with thread data
 * @param threadPreviews - Array of thread preview objects containing subject information, can be null/undefined
 * @param isDeferringLoadingThreadPreviews - Flag indicating whether to use deferred loading logic
 *
 * @returns void - This hook mutates the initialMarkers object directly as a side effect
 *
 * @example
 * ```typescript
 * useStitchSubjectIntoThreads(
 *   markers,
 *   diffSummaries,
 *   threadPreviews,
 *   true // using deferred loading
 * );
 * ```
 */
export function useStitchSubjectIntoThreads(
  initialMarkers: Markers | undefined,
  initialDiffSummaries: PullRequestFileTreeDiff[],
  threadPreviews: ThreadPreview[] | null | undefined,
  isDeferringLoadingThreadPreviews: boolean,
): void {
  useMemo(() => {
    if (isDeferringLoadingThreadPreviews) {
      if (!initialMarkers || !initialDiffSummaries) return

      for (const diffSummary of initialDiffSummaries) {
        if (!diffSummary.markersMap) continue

        for (const markerKey in diffSummary.markersMap) {
          const diffSummaryMarker = diffSummary.markersMap[markerKey]
          if (!diffSummaryMarker?.threads) continue

          for (const threadMarker of diffSummaryMarker.threads) {
            const subject = getSubjectValuesFromThreadMarkers(markerKey, threadMarker)
            const thread = initialMarkers.threads[Number(threadMarker.id)]
            if (thread && subject) {
              // TODO: This is a workaround to avoid the ESLint error about mutating props directly.
              // Ideally, we should refactor this to avoid direct mutation, but since we need to do a quick fix for performance, this mimics the previous behavior.
              // eslint-disable-next-line react-hooks/react-compiler
              thread.subject = subject
            }
          }
        }
      }
    } else {
      for (const [_, value] of Object.entries(initialMarkers?.threads ?? {})) {
        if (value) {
          const threadPreviewWithId = (threadPreviews ?? []).filter(
            //TODO: we should update the types to represent the fact that the thread.id value is a number not a string
            thread => parseInt(thread.threadId) === (value.id as unknown as number),
          )
          if (threadPreviewWithId.length === 1) {
            value.subject = threadPreviewWithId[0]?.subject
          }
        }
      }
      return initialMarkers
    }
  }, [initialDiffSummaries, initialMarkers, isDeferringLoadingThreadPreviews, threadPreviews])
}

const diffSideMap = {
  L: 'LEFT',
  R: 'RIGHT',
} as const

type LineSideLetter = 'L' | 'R'

function isLineSideLetter(value: string | undefined): value is LineSideLetter {
  return value === 'L' || value === 'R'
}

function getSubjectValuesFromThreadMarkers(
  diffSummaryKey: string,
  threadMarker: ThreadMarker,
): ThreadSubject | undefined {
  if (diffSummaryKey === 'FILE') {
    return undefined
  }

  const endDiffSideChar = diffSummaryKey[0]
  if (!isLineSideLetter(endDiffSideChar)) {
    throw new Error(`Invalid diff side character: ${endDiffSideChar}`)
  }
  const endDiffSideLetter = endDiffSideChar
  const endLineNumber = Number(diffSummaryKey.slice(1))

  let startLineNumber = -1
  let startDiffSideLetter: LineSideLetter = 'R'

  if (threadMarker.start) {
    startLineNumber = parseInt(threadMarker.start.slice(1))
    const startChar = threadMarker.start[0]
    if (isLineSideLetter(startChar)) {
      startDiffSideLetter = startChar
    }
  } else {
    startLineNumber = endLineNumber
    startDiffSideLetter = endDiffSideLetter
  }

  const endDiffSide = diffSideMap[endDiffSideLetter]
  const startDiffSide = diffSideMap[startDiffSideLetter]

  return {
    endLine: endLineNumber,
    endDiffSide,
    startLine: startLineNumber,
    startDiffSide,
  }
}
